
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';

interface NavbarProps {
  onSearch?: (query: string) => void;
}

const Navbar = ({ onSearch }: NavbarProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
    };

    fetchUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    // Add scroll event listener to detect when the navbar should change color
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchQuery);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Déconnexion réussie');
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Erreur lors de la déconnexion');
    }
  };

  return (
    <header className={`border-b sticky top-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-gradient-to-r from-primary/90 via-secondary/90 to-primary/90 backdrop-blur-sm shadow-md' 
        : 'bg-gradient-to-r from-primary/80 to-secondary/80'
    }`}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="font-bold text-xl text-white flex items-center group">
              <img 
                src="/lovable-uploads/c5e9bf5c-1418-4e99-b5e6-e0a55f0aede9.png" 
                alt="Mucodec Logo" 
                className="h-24 w-auto mr-2 transition-transform duration-300 group-hover:scale-110 border-2 border-white-500 rounded-lg shadow-md " 

              /> 
              <span className="hidden md:inline relative overflow-hidden after:content-[''] after:absolute after:h-0.5 after:w-full after:bg-white after:left-0 after:bottom-0 after:transform after:translate-x-[-100%] after:transition-transform after:duration-300 group-hover:after:translate-x-0">
                Mucodec Alerte licence 
              </span>
            </Link>
            <nav className="hidden md:flex space-x-4">
              <Link 
                to="/" 
                className="text-white opacity-90 hover:opacity-100 transition-all duration-300 relative after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:w-0 after:bg-white after:mx-auto after:transition-all hover:after:w-full"
              >
                Accueil
              </Link>
              {user && (
                <Link 
                  to="/admin" 
                  className="text-white opacity-90 hover:opacity-100 transition-all duration-300 font-medium relative after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:w-0 after:bg-white after:mx-auto after:transition-all hover:after:w-full"
                >
                  Administration
                </Link>
              )}
            </nav>
          </div>

          <div className="flex items-center space-x-4">
            {onSearch && (
              <form onSubmit={handleSearch} className="hidden sm:flex w-full max-w-sm items-center">
                <Input
                  type="search"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/20 border-white/30 text-white placeholder:text-white/70 focus:bg-white/30"
                />
              </form>
            )}
            
            <div className="flex items-center space-x-2">
              {user ? (
                <>
                  <span className="text-sm hidden md:inline text-white">
                    {user.email}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSignOut}
                    className="border-white/40 text-black hover:bg-black/20 hover:text-black transition-all duration-300"
                  >
                    Déconnexion
                  </Button>
                  <Button 
                    variant="default" 
                    size="sm" 
                    asChild
                    className="bg-black text-primary hover:bg-white/80 transition-all duration-300 animate-pulse"
                  >
                    <Link to="/admin">Administration</Link>
                  </Button>
                </>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  asChild
                  className="border-white/40 text-black hover:bg-white/20 hover:text-black transition-all duration-300"
                >
                  <Link to="/auth">Connexion</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {onSearch && (
        <div className="sm:hidden p-3 border-t border-white/20">
          <form onSubmit={handleSearch}>
            <Input
              type="search"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white/20 border-white/30 text-white placeholder:text-white/70"
            />
          </form>
        </div>
      )}
    </header>
  );
};

export default Navbar;
